import BreadcrumbsComponent from "@/components/Breadcrumbs";
import NoPermissionInfo from "@/components/NoPermissionInfo";
import ShimmerLoader from "@/components/common/ShimmerLoader";
import MessageModal from "@/components/common/modals/MessageModal";
import ConfirmationModal from "@/components/common/modals/ConfirmationModal";
import ShimmerDataGrid from "@/components/common/shimmer-data-grid";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectSelectedSiteId } from "@/store/selectors";
import { usePermissions } from "@/hooks/usePermissions";
import { fetchSiteDocumentation } from "@/store/slices/siteDocumentationSlice";
import SiteDocumentationService from "@/services/siteDocumentation";
import Grid from "@mui/material/Grid";
import type { GridPaginationModel } from "@mui/x-data-grid";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SiteDocumentationHeader from "@/components/siteDocumentation/SiteDocumentationHeader";
import SiteDocumentationGrid from "@/components/siteDocumentation/SiteDocumentationGrid";
import AddSiteDocumentationModal from "@/components/siteDocumentation/AddSiteDocumentationModal";
import DeleteSiteDocumentationConfirm from "@/components/siteDocumentation/DeleteSiteDocumentationConfirm";
import { TimelineService } from "@/services/TimelineService";
import { useAuth0 } from "@auth0/auth0-react";

const SiteDocumentation = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { getFeatureActions } = usePermissions();

  // Permissions
  const viewSiteDocTabPermission = getFeatureActions(
    "Site Documentation",
    "View Site Documentation Tab",
    "Site Documentation Page",
  );
  const editUnownedPermission = getFeatureActions(
    "Site Documentation",
    "Edit Unowned Site Documentation",
    "Site Documentation Page",
  );

  // Redux selectors
  const selectedSiteId = useAppSelector(selectSelectedSiteId);
  const { data: userData } = useAppSelector((state) => state.user);
  const { status, fetched } = useAppSelector(
    (state) => state.siteDocumentation,
  );

  // Shared pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // Sort state for server-side sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null,
  );
  const { getAccessTokenSilently } = useAuth0();

  // Page-level UI state only
  const [filters, setFilters] = useState<any>(null);
  const [editingDocumentation, setEditingDocumentation] = useState<any>(null);
  const [addDocumentationModalOpen, setAddDocumentationModalOpen] =
    useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentationToDelete, setDocumentationToDelete] = useState<Array<{
    id: string;
    name: string;
  }> | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [noPermissionModalOpen, setNoPermissionModalOpen] = useState(false);
  const [noPermissionMessage, setNoPermissionMessage] = useState("");
  const [ownershipConfirmOpen, setOwnershipConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: "edit" | "delete";
    row: any;
    allRows?: any[];
  } | null>(null);
  // Centralized fetch function - only place that fetches site documentation data
  const fetchData = useCallback(async () => {
    if (selectedSiteId && userData?.id) {
      try {
        const accessToken = await getAccessTokenSilently();    
        dispatch(
          fetchSiteDocumentation({
            userId: userData.id,
            siteId: selectedSiteId,
            page: paginationModel.page + 1, // API uses 1-based page indexing
            pageSize: paginationModel.pageSize,
            sortBy: sortField || undefined,
            sortOrder: sortDirection,
          }),
        );
      } catch (error) {
        console.error("Failed to fetch site documentation:", error);
      }
    }
  }, [
    selectedSiteId,
    userData?.id,
    dispatch,
    paginationModel.page,
    paginationModel.pageSize,
    sortField,
    sortDirection,
  ]);
  // Initial data fetch - only if not already loaded or if site/user changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers for grid callbacks
  const handleEditDocumentation = (rowId: string, allRows: any[]) => {
    const rowToEdit = allRows.find((row) => row.id === rowId);
    if (rowToEdit) {
      const currentUserId = String(userData?.id || "");
      const originalUploaderId = String(rowToEdit.modified_by || "");
      if (originalUploaderId && currentUserId !== originalUploaderId) {
        if (!editUnownedPermission.canView) {
          setNoPermissionMessage(
            t(
              "noEditPermission",
              "This document was originally uploaded by another user. You do not have permissions to edit it.",
            ),
          );
          setNoPermissionModalOpen(true);
        } else {
          setPendingAction({ type: "edit", row: rowToEdit });
          setOwnershipConfirmOpen(true);
        }
      } else {
        setEditingDocumentation(rowToEdit);
        setAddDocumentationModalOpen(true);
      }
    }
  };

  const handleDeleteDocumentation = (rowId: string, allRows: any[]) => {
    console.log("handleDeleteDocumentation called with:", rowId, allRows);
    const rowToDelete = allRows.find((row) => row.id === rowId);
    console.log("rowToDelete found:", rowToDelete);
    if (rowToDelete) {
      const currentUserId = String(userData?.id || "");
      const originalUploaderId = String(rowToDelete.modified_by || "");
      if (originalUploaderId && currentUserId !== originalUploaderId) {
        setPendingAction({ type: "delete", row: rowToDelete, allRows });
        setOwnershipConfirmOpen(true);
      } else {
        setDocumentationToDelete([
          { id: rowToDelete.id, name: rowToDelete.name },
        ]);
        console.log("Setting deleteConfirmOpen to true");
        setDeleteConfirmOpen(true);
      }
    }
  };

  const handleBulkDelete = (allRows: any[]) => {
    console.log("handleBulkDelete called with:", allRows);
    // This will be called by grid with filtered rows
    setDocumentationToDelete(
      allRows.map((row) => ({ id: row.id, name: row.name })),
    );
    console.log("Setting deleteConfirmOpen to true for bulk delete");
    setDeleteConfirmOpen(true);
  };

  const handleDownloadDocumentation = async (rowId: string, allRows: any[]) => {
    try {
      const rowToDownload = allRows.find((row) => row.id === rowId);
      if (!rowToDownload) {
        throw new Error("Document not found");
      }
      console.log("Downloading document:", rowToDownload);
      const timelineService = new TimelineService();
      const { downloadUrl } = await timelineService.generateDownloadUrl(
        "documents", // container name - adjust if needed
        rowToDownload.file_name,
        rowToDownload.file_name,
      );

      // // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.download = rowToDownload.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download documentation:", error);
      setSuccessMessage(t("downloadFailed", "Failed to download document"));
      setSuccessModalOpen(true);
    }
  };

  const handleDeleteSuccess = async (count: number) => {
    setDeleteConfirmOpen(false);
    setDocumentationToDelete(null);

    // Refetch data after successful deletion
    await fetchData();

    const message =
      count > 1
        ? t("docsDeletedSuccess", "Site documents deleted successfully.")
        : t("docDeletedSuccess", "Site document deleted successfully.");
    setSuccessMessage(message);
    setSuccessModalOpen(true);
  };

  const handleClose = () => {
    setAddDocumentationModalOpen(false);
    setEditingDocumentation(null);
  };

  const handleSuccess = async () => {
    const message = editingDocumentation
      ? t("docUpdatedSuccess", "Site document updated successfully.")
      : t("docCreatedSuccess", "Site document created successfully.");
    setSuccessMessage(message);
    handleClose();
    setSuccessModalOpen(true);
    // Refetch data after successful create/update
    await fetchData();
  };

  const filterDocumentation = async (filters: any) => {
    setFilters(filters);
  };

  const handleSortChange = (
    field: string | null,
    direction: "asc" | "desc" | null,
  ) => {
    setSortField(field);
    setSortDirection(direction);
    if (field && direction) {
      console.log(
        `SiteDocumentation: Sort applied: column = ${field}, direction = ${direction}`,
      );
    } else {
      console.log("SiteDocumentation: No sort applied");
    }
  };

  const handleOwnershipConfirm = () => {
    if (!pendingAction) return;

    if (pendingAction.type === "edit") {
      setEditingDocumentation(pendingAction.row);
      setAddDocumentationModalOpen(true);
    } else if (pendingAction.type === "delete") {
      setDocumentationToDelete([
        { id: pendingAction.row.id, name: pendingAction.row.name },
      ]);
      setDeleteConfirmOpen(true);
    }

    setOwnershipConfirmOpen(false);
    setPendingAction(null);
  };

  const handleOwnershipCancel = () => {
    setOwnershipConfirmOpen(false);
    setPendingAction(null);
  };

  return (
    <>
      {!viewSiteDocTabPermission.canView ? (
        <div className="page page-site-documentation">
          <BreadcrumbsComponent />
          <div className="page-content">
            <NoPermissionInfo />
          </div>
        </div>
      ) : (
        <div className="page page-site-documentation">
          <BreadcrumbsComponent />
          <div className="page-content">
            <div data-testid="site-documentation-root">
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Suspense
                    fallback={
                      <ShimmerLoader variant="text" lines={3} height="14px" />
                    }
                  >
                    <div data-lazy-component="SiteDocumentationHeader">
                      <SiteDocumentationHeader
                        filterDocumentation={filterDocumentation}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        onCreateDocumentationClick={() =>
                          setAddDocumentationModalOpen(true)
                        }
                      />
                    </div>
                  </Suspense>
                </Grid>
              </Grid>
              <Suspense fallback={<ShimmerDataGrid />}>
                <div data-lazy-component="SiteDocumentationGrid">
                  <SiteDocumentationGrid
                    filters={filters}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onEditDocumentation={handleEditDocumentation}
                    onDeleteDocumentation={handleDeleteDocumentation}
                    onBulkDelete={handleBulkDelete}
                    onDownloadDocumentation={handleDownloadDocumentation}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSortChange={handleSortChange}
                  />
                </div>
              </Suspense>
            </div>
          </div>
          {addDocumentationModalOpen && (
            <AddSiteDocumentationModal
              open={addDocumentationModalOpen}
              onClose={handleClose}
              onSuccess={handleSuccess}
              existingDocumentation={editingDocumentation}
            />
          )}
          {deleteConfirmOpen && (
            <DeleteSiteDocumentationConfirm
              open={deleteConfirmOpen}
              documents={documentationToDelete || []}
              onClose={() => setDeleteConfirmOpen(false)}
              onSuccess={handleDeleteSuccess}
            />
          )}
          {successModalOpen && (
            <MessageModal
              open={successModalOpen}
              onClose={() => setSuccessModalOpen(false)}
              title={t("success", "Success")}
              message={successMessage}
              buttonText={t("OK")}
            />
          )}
          {ownershipConfirmOpen && (
            <ConfirmationModal
              open={ownershipConfirmOpen}
              onClose={handleOwnershipCancel}
              onConfirm={handleOwnershipConfirm}
              title={
                pendingAction?.type === "delete"
                  ? t("deleteDocumentation", "Delete Documentation")
                  : t("editDocumentation", "Edit Documentation")
              }
              message={
                pendingAction?.type === "delete"
                  ? t(
                      "deleteDocumentNotOwnedWarning",
                      "This document was originally uploaded by another user. Please confirm that you wish to proceed with deleting.",
                    )
                  : t(
                      "documentNotOwnedWarning",
                      "This document was originally uploaded by another user. Please confirm that you wish to proceed with editing.",
                    )
              }
              confirmText={t("proceed", "Proceed")}
              cancelText={t("cancel", "Cancel")}
            />
          )}
        </div>
      )}
    </>
  );
};

export default SiteDocumentation;
